import { InputOnChangeData, AnvilSelectOptionsProps, debounce } from '@servicetitan/design-system';
import { observable, action, runInAction, makeObservable } from 'mobx';
import { ChangeEvent } from 'react';

const defaultOptionsFilter = (search: string) => (option: AnvilSelectOptionsProps) =>
    option.text?.toLowerCase().includes(search.toLowerCase());

export class SelectNewState {
    @observable loading = false;
    @observable search = '';
    @observable allOptions: AnvilSelectOptionsProps[] = [];
    @observable flatOptions: AnvilSelectOptionsProps[] = [];
    @observable options: AnvilSelectOptionsProps[] = [];

    constructor(
        options: AnvilSelectOptionsProps[],
        private optionsFilter = defaultOptionsFilter
    ) {
        makeObservable(this);

        this.allOptions = options;
        this.flatOptions = this.flattenOptions(options);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    onSearchChange = (() => {
        const fetch = debounce(() => this.fetch(), 100);
        return (_0: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
            runInAction(() => {
                this.search = data.value;
            });
            fetch();
        };
    })();

    @action
    setOptions = (options: AnvilSelectOptionsProps[]) => {
        this.allOptions = options;
        this.flatOptions = this.flattenOptions(options);
        this.fetch();
    };

    @action
    setSearch = (search: string) => {
        this.search = search;
        this.fetch();
    };

    @action
    fetch = () => {
        this.options = [];
        this.loading = true;

        let filteredOptions: AnvilSelectOptionsProps[] = [];
        if (!this.search) {
            filteredOptions = this.allOptions;
        } else {
            this.allOptions.forEach(option => {
                const filter = this.optionsFilter(this.search.toLowerCase());

                if (filter(option)) {
                    filteredOptions.push(option);
                } else if (option.options) {
                    const filteredSubOptions = option.options.filter(o => filter(o));
                    if (filteredSubOptions.length) {
                        filteredOptions.push({
                            ...option,
                            options: filteredSubOptions,
                        });
                    }
                }
            });
        }

        runInAction(() => {
            this.options = filteredOptions;
            this.loading = false;
        });
    };

    private flattenOptions(options: AnvilSelectOptionsProps[]): AnvilSelectOptionsProps[] {
        const flatOptions: AnvilSelectOptionsProps[] = [];
        options.forEach(option => {
            if (option.options) {
                flatOptions.push(...this.flattenOptions(option.options));
            } else {
                flatOptions.push(option);
            }
        });
        return flatOptions;
    }
}
